import {
  AbilityAction,
  AbilitySubjectType,
  RawPermissions,
} from '@kinet-open-network/shared/data-access/model';
import { dataSet } from '@kinet-open-network/shared/util';

export type CategorizedPermissions = {
  all_manage: RawPermissions[number];
  all_action: {
    [key in AbilityAction]: RawPermissions[number];
  };
  subject_manage: {
    [key in AbilitySubjectType]: RawPermissions[number];
  };
  subject_action: {
    [key in AbilitySubjectType]: {
      [key in AbilityAction]: RawPermissions[number];
    };
  };
};

/**
 * Merge permissions by priority
 *
 * ```md
 * Prioriy is as follows (from lowest to highest):
 *  1. "manage/all"
 *  2. "[action]/all"
 *  3. "manage/[subject]"
 *  4. "[action]/[subject]
 * ```
 *
 * @param permissions Permissions to merge
 * @returns Merged permissions
 */
export function mergePermissions(permissions: RawPermissions) {
  const categorized = categorizePermissions(permissions);
  const merged: RawPermissions = [];

  // only add all_manage if it is not inverted
  if (categorized.all_manage && categorized.all_manage.inverted !== true) {
    merged.push(categorized.all_manage);
  }

  if (categorized.all_action) {
    merged.push(...Object.values(categorized.all_action));
  }

  if (categorized.subject_manage) {
    merged.push(...Object.values(categorized.subject_manage));
  }

  if (categorized.subject_action) {
    merged.push(
      ...Object.values(categorized.subject_action).flatMap(Object.values),
    );
  }

  return merged;
}

export function categorizePermissions(permissions: RawPermissions) {
  return permissions.reduce((acc, permission) => {
    if (permission.subject === 'all' && permission.action === 'manage') {
      return dataSet(acc, 'all_manage', permission);
    } else if (permission.subject === 'all' && permission.action !== 'manage') {
      return dataSet(acc, `all_action.${permission.action}`, permission);
    } else if (permission.action === 'manage' && permission.subject !== 'all') {
      return dataSet(acc, `subject_manage.${permission.subject}`, permission);
    }

    return dataSet(
      acc,
      `subject_action.${permission.subject}.${permission.action}`,
      permission,
    );
  }, {} as CategorizedPermissions);
}
